import React, { useState } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const ImageUpload = ({ 
  currentImage, 
  onImageUpload, 
  onImageRemove,
  label = "Background Image",
  folder = "featured-content",
  disabled = false,
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const { user } = useAuth();

  const validateFile = (file, maxSizeBytes = 10 * 1024 * 1024) => { // 10MB default
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    
    if (!allowedTypes?.includes(file?.type)) {
      return { isValid: false, error: 'Invalid file type. Please upload JPEG, PNG, WebP, or GIF images.' };
    }

    if (file?.size > maxSizeBytes) {
      const maxSizeMB = maxSizeBytes / (1024 * 1024);
      return { isValid: false, error: `File too large. Maximum size is ${maxSizeMB}MB.` };
    }

    return { isValid: true, error: null };
  };

  const optimizeImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        // Draw and compress
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (file) => {
    if (!user) {
      setUploadError('Please sign in to upload images');
      return;
    }

    // Validate file
    const validation = validateFile(file, 10 * 1024 * 1024); // 10MB max
    if (!validation?.isValid) {
      setUploadError(validation?.error);
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      // Optimize image before upload
      const optimizedFile = await optimizeImage(file, 1920, 0.85);
      
      // Upload to featured-images bucket
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${folder}/${user?.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase?.storage?.from('featured-images')?.upload(fileName, optimizedFile);

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase?.storage?.from('featured-images')?.getPublicUrl(fileName);

      // Call parent callback with the new image URL
      onImageUpload?.({
        url: publicUrl,
        path: fileName
      });

    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError(error?.message === 'Failed to fetch'? 'Cannot connect to storage service. Please check your connection.': 'Failed to upload image. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (currentImage?.path) {
      try {
        await supabase?.storage?.from('featured-images')?.remove([currentImage?.path]);
      } catch (error) {
        console.error('Error removing image:', error);
      }
    }
    onImageRemove?.();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragActive(false);

    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileSelect(files?.[0]);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files?.length > 0) {
      handleFileSelect(files?.[0]);
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-foreground mb-2">
        {label}
      </label>

      {currentImage?.url ? (
        <div className="space-y-3">
          {/* Current Image Preview */}
          <div className="relative">
            <img 
              src={currentImage?.url} 
              alt="Current background" 
              className="w-full h-40 object-cover rounded-lg border border-border"
            />
            <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRemoveImage}
                disabled={disabled || uploading}
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Remove
              </Button>
            </div>
          </div>

          {/* Replace Image Option */}
          <div className="text-center">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
                disabled={disabled || uploading}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || uploading}
                asChild
              >
                <span>
                  <Icon name="Upload" size={16} className="mr-2" />
                  Replace Image
                </span>
              </Button>
            </label>
          </div>
        </div>
      ) : (
        <>
          {/* Upload Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-colors
              ${dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/50'}
            `}
          >
            {uploading ? (
              <div className="space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-sm text-muted-foreground">Uploading image...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Icon name="Upload" className="text-muted-foreground mx-auto" size={32} />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Drop an image here or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Supports JPEG, PNG, WebP, GIF up to 10MB
                  </p>
                </div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={disabled || uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || uploading}
                    asChild
                  >
                    <span>Browse Files</span>
                  </Button>
                </label>
              </div>
            )}
          </div>

          {/* Manual URL Input Option */}
          <div className="text-center text-xs text-muted-foreground mt-2">
            <span>or provide a URL:</span>
          </div>
          <Input
            type="url"
            placeholder="https://example.com/image.jpg"
            onChange={(e) => onImageUpload?.({ url: e?.target?.value, path: null })}
            disabled={disabled || uploading}
          />
        </>
      )}

      {/* Error Display */}
      {uploadError && (
        <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
          {uploadError}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;